import React from 'react';
import { Helmet } from 'react-helmet-async';
import Typography from '@mui/material/Typography';
import { Button, Grid, IconButton, Link, Stack, Alert, CircularProgress } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import DialogCalendar from '../../components/dialog/dialogcalendar';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { getToken } from '../../utils/common';
import logger from '../../utils/logger';
import useMediaQuery from '@mui/material/useMediaQuery';

export default function CalendarPage() {
  const currentDate = new Date();
  const [year, setYear] = React.useState(currentDate.getFullYear());
  const currentMonthNumber = currentDate.getMonth() + 1;
  const [month, setMonth] = React.useState(currentMonthNumber);
  const daysArray = getDaysArrayByMonth(year, month);
  const [data, setData] = React.useState([]);
  const [calendardialog, setCalendarDialog] = React.useState({
    open: false,
    data: [],
    date: '',
  });
  const [search, setSearch] = React.useState('');
  const [fetchError, setFetchError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const fetchInfo = (url) => {
    return fetch(url, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    })
      .then((res) => res.json())
      .then((d) => setData(d.data))
      .catch((error) => {
        logger.error('Fallback calendar fetch failed:', error);
        setFetchError(t('CalendarPrompt.error', { defaultValue: 'Unable to load calendar data. Please try again.' }));
        throw error;
      });
  };

  // New function to fetch current month and next month data
  const fetchInfoWithNextMonth = (currentMonth, currentYear) => {
    // Calculate next month and year
    let nextMonth = currentMonth + 1;
    let nextYear = currentYear;

    if (nextMonth > 12) {
      nextMonth = 1;
      nextYear = currentYear + 1;
    }

    // Create URLs for both months
    const currentMonthUrl = `${window.REACT_APP_API_PURE}calendar/list?month=${currentMonth}&year=${currentYear}`;
    const nextMonthUrl = `${window.REACT_APP_API_PURE}calendar/list?month=${nextMonth}&year=${nextYear}`;

    // Fetch both months in parallel
    setLoading(true);
    setFetchError('');
    Promise.all([
      fetch(currentMonthUrl, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }).then((res) => res.json()),
      fetch(nextMonthUrl, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }).then((res) => res.json()),
    ])
      .then(([currentData, nextData]) => {
        // Merge data from both months
        const allData = [...(currentData.data || []), ...(nextData.data || [])];

        // Remove duplicates based on id AND time_start (since same id can have different times)
        const uniqueData = allData.reduce((acc, current) => {
          const existingItem = acc.find((item) => item.id === current.id && item.time_start === current.time_start);
          if (!existingItem) {
            acc.push(current);
          }
          return acc;
        }, []);


        // Check for duplicates by date and title
        const duplicates = uniqueData.reduce((acc, item, index) => {
          const date = new Date(item.time_start).toDateString();
          const key = `${date}-${item.title}`;
          if (acc[key]) {
            acc[key].push({ index, item });
          } else {
            acc[key] = [{ index, item }];
          }
          return acc;
        }, {});

        // Log any duplicates found
        Object.entries(duplicates).forEach(([key, items]) => {
          if (items.length > 1) {
            logger.warn(`Duplicate entries found for ${key}:`, items);
          }
        });

        setData(uniqueData);
      })
      .catch((error) => {
        logger.error('Error fetching calendar data:', error);
        setFetchError(t('CalendarPrompt.error', { defaultValue: 'Unable to load calendar data. Please try again.' }));
        fetchInfo(currentMonthUrl);
      })
      .finally(() => setLoading(false));
  };
  const handlemonth = (e) => {
    if (month >= 12) {
      setYear(year + 1);
      setMonth(1);
      fetchInfoWithNextMonth(1, year + 1);
    } else {
      setMonth(month + 1);
      fetchInfoWithNextMonth(month + 1, year);
    }
  };
  const handlemonthback = (e) => {
    if (month <= 1) {
      setYear(year - 1);
      setMonth(12);
      fetchInfoWithNextMonth(12, year - 1);
    } else {
      setMonth(month - 1);
      fetchInfoWithNextMonth(month - 1, year);
    }
  };

  React.useEffect(() => {
    fetchInfoWithNextMonth(month, year);
  }, []);

  function Refresh() {
    fetchInfoWithNextMonth(month, year);
  }

  function getDaysArrayByMonth(year, month) {
    const daysArray = [];
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);

    for (let d = new Date(startDate); d <= endDate; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
      daysArray.push(d);
    }

    return daysArray;
  }
  function getDayName(date) {
    const days = [t('Day.1'), t('Day.2'), t('Day.3'), t('Day.4'), t('Day.5'), t('Day.6'), t('Day.7')];
    const dayIndex = date.getDay();
    // Prevent NaN values in Firefox
    return isNaN(dayIndex) ? days[0] : days[dayIndex];
  }
  function getDayNumber(date) {
    const dayNumber = date.getDate();
    // Prevent NaN values in Firefox
    return isNaN(dayNumber) ? 0 : dayNumber;
  }
  function formatDate(date) {
    // Handle both Date objects and date strings
    const dateObj = date instanceof Date ? date : new Date(date);

    // Prevent NaN values in Firefox
    if (isNaN(dateObj.getTime())) {
      return '00-00-0000';
    }

    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();

    return `${day}-${month}-${year}`;
  }

  function DayCard({ date, index }) {
    const isCurrentDate = date.toDateString() === currentDate.toDateString();

    const filteredData = data
      ?.filter((d) => {
        // First filter out items with empty schedules
        if (!d.schedule || d.schedule.trim() === '') {
          return false;
        }

        // Parse the API date and extract just the date part (YYYY-MM-DD) to avoid timezone issues
        const apiDateString = d.time_start.split(' ')[0]; // Get "2025-10-18" from "2025-10-18 22:38:42Z"
        const apiDate = new Date(apiDateString + 'T00:00:00'); // Create date at midnight to avoid timezone issues

        const calendarDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        // Compare dates by year, month, and day only (ignore time)
        const matches =
          apiDate.getFullYear() === calendarDate.getFullYear() &&
          apiDate.getMonth() === calendarDate.getMonth() &&
          apiDate.getDate() === calendarDate.getDate();

        if (matches) {
          // Date matches
        }

        return matches;
      })
      .sort((a, b) => {
        const timeStartA = new Date(a.time_start);
        const timeStartB = new Date(b.time_start);
        return timeStartA - timeStartB;
      });
    return (
      <div
        onClick={() => {
          if (filteredData != undefined) {
            setCalendarDialog({
              open: true,
              data: filteredData,
              date: isCurrentDate,
            });
          }
        }}
        style={{ height: 185, border: '1px solid #E7E7E7', padding: '10px', width: '100%' }}
      >
        {index == 0 ? <span style={{ color: '#888888' }}>{getDayName(date)}</span> : ''}
        <div
          style={{
            marginLeft: 20,
            backgroundColor: isCurrentDate ? '#1EB3D9' : 'transparent',
            color: isCurrentDate ? 'white' : index == 28 && getDayNumber(date) < 28 ? '#B0B0B0' : 'black',
            display: isCurrentDate ? 'inline-flex' : '',
            padding: isCurrentDate ? 2 : '',
            borderRadius: 5,
          }}
        >
          {getDayNumber(date)}
        </div>
        {filteredData?.length == 0 || filteredData == undefined ? (
          ''
        ) : (
          <div>
            {' '}
            {filteredData
              ?.filter((d) => {
                if (search == '') {
                  return d;
                } else {
                  return d.title.toLowerCase().includes(search.toLowerCase());
                }
              })
              .map((d, index) => {
                if (index < 3) {
                  return (
                    <div>
                      <Typography
                        variant="caption"
                        style={{
                          borderRadius: 5,
                          padding: 5,
                          backgroundColor:
                            d.schedule == 'Daily' ? '#1EB3D9' : d.schedule == 'Weekly' ? '#8667EB' : '#F9A207',
                          width: '100%',
                          display: 'flex',
                          placeItems: 'center',
                          color: 'white',
                          cursor: 'pointer',
                        }}
                      >
                        <FolderCopyIcon fontSize="5px" /> {d.title.length > 10 ? `${d.title.slice(0, 10)}...` : d.title}
                      </Typography>

                      <div style={{ paddingTop: 5 }} />
                    </div>
                  );
                } else {
                  return null;
                }
              })}
            {}
            <div />
            {filteredData.length <= 3 ? (
              ''
            ) : (
              <Link
                onClick={() => {
                  setCalendarDialog({
                    open: true,
                    data: filteredData,
                    date: isCurrentDate,
                  });
                }}
              >
                <Typography style={{ cursor: 'pointer' }} variant="caption">
                  {filteredData.length - 3} more
                </Typography>
              </Link>
            )}{' '}
          </div>
        )}
      </div>
    );
  }

  function DayList({ daysArray }) {
    const rows = [];

    const getDaysArrayByMonth = (year, month) => {
      const daysInMonth = new Date(year, month, 0).getDate();
      return Array.from({ length: daysInMonth }, (_, i) => new Date(year, month - 1, i + 1));
    };

    const remainingDays = 35 - daysArray.length;

    const nextMonth = getDaysArrayByMonth(year, month + 1);

    const paddedDaysArray = [...daysArray, ...nextMonth.slice(0, remainingDays)];

    for (let i = 0; i < paddedDaysArray.length; i += 7) {
      const rowDays = paddedDaysArray.slice(i, i + 7);
      rows.push(
        <div style={{ display: 'flex' }} key={i}>
          {rowDays.map((day, index) => {
            return <DayCard key={index} date={day} index={i} />;
          })}
        </div>
      );
    }

    return <div>{rows}</div>;
  }
  const { t } = useTranslation();

  function handleClickLang(lang) {
    i18next.changeLanguage(lang);
  }
  function getMonthName(monthNumber) {
    const months = [
      t('Month.1'),
      t('Month.2'),
      t('Month.3'),
      t('Month.4'),
      t('Month.5'),
      t('Month.6'),
      t('Month.7'),
      t('Month.8'),
      t('Month.9'),
      t('Month.10'),
      t('Month.11'),
      t('Month.12'),
    ];

    if (monthNumber >= 1 && monthNumber <= 12) {
      return months[monthNumber - 1];
    } else {
      return 'Invalid Month';
    }
  }
  function handleClose() {
    setCalendarDialog((current) => ({
      ...current,
      open: false,
    }));
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <Helmet>
        <title>Calendar | COVAS</title>
      </Helmet>
      {
        <DialogCalendar
          open={calendardialog.open}
          data={calendardialog.data}
          handleClose={handleClose}
          date={calendardialog.date}
          refresh={Refresh}
        />
      }
      {fetchError && (
        <Alert severity="error" onClose={() => setFetchError('')}>
          {fetchError}
        </Alert>
      )}
      {isMobile ? (
        <Stack spacing={2}>
          <Typography variant="h4">
            {getMonthName(month)} {year}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton style={{ color: '#12A2CE' }} onClick={handlemonthback}>
              <ArrowBackIosIcon />
            </IconButton>
            <IconButton style={{ color: '#12A2CE' }} onClick={handlemonth}>
              <ArrowForwardIosIcon />
            </IconButton>
            <Button
              onClick={() => {
                const currentMonth = currentDate.getMonth() + 1;
                const currentYear = currentDate.getFullYear();
                setYear(currentYear);
                setMonth(currentMonth);
                fetchInfoWithNextMonth(currentMonth, currentYear);
              }}
              variant="outlined"
            >
              {t('Today.1')}
            </Button>
          </Stack>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label={t('Search.1')}
            onChange={(e) => setSearch(e.target.value)}
            value={search}
            InputProps={{
              endAdornment: (
                <IconButton type="submit" aria-label="search">
                  <SearchIcon />
                </IconButton>
              ),
            }}
          />
          <Button onClick={Refresh} variant="contained" className="bg-cyan-500">
            Refresh
          </Button>
        </Stack>
      ) : (
        <Grid container direction="row" alignItems="center">
          <Grid item xs={3}>
            <Typography variant="h4" sx={{ p: 2, paddingLeft: 5 }}>
              {getMonthName(month)} {year}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <IconButton style={{ color: '#12A2CE' }} onClick={handlemonthback}>
              <ArrowBackIosIcon />
            </IconButton>
            <IconButton style={{ color: '#12A2CE' }} onClick={handlemonth}>
              <ArrowForwardIosIcon />
            </IconButton>
            <Button
              sx={{ ml: 1 }}
              onClick={() => {
                const currentMonth = currentDate.getMonth() + 1;
                const currentYear = currentDate.getFullYear();
                setYear(currentYear);
                setMonth(currentMonth);
                fetchInfoWithNextMonth(currentMonth, currentYear);
              }}
              variant="outlined"
            >
              {t('Today.1')}
            </Button>
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              label={t('Search.1')}
              onChange={(e) => setSearch(e.target.value)}
              value={search}
              InputProps={{
                endAdornment: (
                  <IconButton type="submit" aria-label="search">
                    <SearchIcon />
                  </IconButton>
                ),
              }}
            />
          </Grid>
          <Grid item xs={2} textAlign="right">
            <Button onClick={Refresh} variant="contained" className="bg-cyan-500">
              Refresh
            </Button>
          </Grid>
        </Grid>
      )}
      {loading && (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
          <CircularProgress size={20} />
          <Typography variant="body2">
            {t('CalendarPrompt.loading', { defaultValue: 'Loading calendar events...' })}
          </Typography>
        </Stack>
      )}

      {}
      <DayList daysArray={daysArray} />
      <div style={{ height: 50 }}></div>
      {}
    </div>
  );
}
